import React from "react"
import pluralize from "pluralize"
import {
    Box,
    componentStyles,
    useTheme,
    VerticalStack,
    ExternalLink,
    HeadingMedium,
    HorizontalStack,
    Text,
} from "@mallardbay/lib-react-components"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFacebookF } from "@fortawesome/free-brands-svg-icons/faFacebookF"
import { faInstagram } from "@fortawesome/free-brands-svg-icons/faInstagram"
import { faTwitter } from "@fortawesome/free-brands-svg-icons/faTwitter"

import type { UserSocialsFieldsFragment } from "~graphql/generated/graphql"

import Avatar, { Size } from "./todo-lib-react-components/avatar"

export default function ProfileCard({
    avatar,
    name,
    position,
    experience,
    bio,
    socials,
}: Props) {
    const styles = useStyles()
    const experienceText = experience
        ? `${pluralize("year", experience, true)} of experience`
        : null

    return (
        <Box style={styles.root}>
            <HorizontalStack>
                <Avatar src={avatar} size={Size.Medium} />
                <VerticalStack style={styles.nameAndExperienceContainer}>
                    <HeadingMedium style={styles.name}>{name}</HeadingMedium>
                    <Box style={styles.experience}>
                        {position && (
                            <Text style={styles.position}>{position}</Text>
                        )}
                        {experienceText && (
                            <Text style={styles.experience}>
                                {experienceText}
                            </Text>
                        )}
                    </Box>
                </VerticalStack>
            </HorizontalStack>
            <Text style={styles.bio}>{bio}</Text>
            {socials && (
                <Box style={styles.socials}>
                    {socials.facebook && (
                        <ExternalLink href={socials.facebook}>
                            <FontAwesomeIcon
                                style={styles.socialIcon}
                                icon={faFacebookF}
                            />
                        </ExternalLink>
                    )}
                    {socials.twitter && (
                        <ExternalLink href={socials.twitter}>
                            <FontAwesomeIcon
                                style={styles.socialIcon}
                                icon={faTwitter}
                            />
                        </ExternalLink>
                    )}
                    {socials.instagram && (
                        <ExternalLink href={socials.instagram}>
                            <FontAwesomeIcon
                                style={styles.socialIcon}
                                icon={faInstagram}
                            />
                        </ExternalLink>
                    )}
                </Box>
            )}
        </Box>
    )
}

interface Props {
    readonly avatar?: string
    readonly name: string
    readonly position: string
    readonly experience?: number | null
    readonly bio?: string | null
    readonly socials?: UserSocialsFieldsFragment | null
}

function useStyles() {
    const { colors, shadows } = useTheme()

    return componentStyles({
        root: {
            backgroundColor: colors.bgColor,
            padding: "1rem",
            borderRadius: "16px",
            borderWidth: "1px",
            borderStyle: "solid",
            borderColor: colors.borderOpaque,
            boxShadow: shadows.lg,
        },
        heading: {
            display: "flex",
            flex: 1,
        },
        avatar: {
            objectFit: "cover",
        },
        nameAndExperienceContainer: {
            gap: 0.4,
            alignSelf: "flex-start",
        },
        name: {
            fontSize: "15px",
            fontWeight: 600,
            marginTop: "0.5rem",
        },
        experience: {
            fontSize: "13px",
            color: colors.contentSecondary,
            marginTop: "0.25rem",
        },
        position: {
            fontSize: "13px",
            fontWeight: 600,
            color: colors.secondaryText,
        },
        bio: {
            fontSize: "14px",
            fontWeight: 300,
            marginBottom: "1rem",
            textAlign: "justify",
            marginTop: "1rem",
            color: colors.secondaryText,
        },
        socials: {
            display: "flex",
            justifyContent: "flex-start",
            gap: "1rem",
        },
        socialIcon: {
            color: colors.contentPrimary,
            ":hover": {
                cursor: "pointer",
                color: colors.contentSecondary,
            },
        },
    })
}
